/**
 * Time calculation for pasting a copied booking onto a new day.
 *
 * A pasted booking must look exactly like the booking it was copied from: the
 * same number of calendar days and the same time-of-day on each edge. The only
 * change is the day the booking starts on. Anything else (e.g. rebuilding the
 * end edge from a millisecond duration, or mixing UTC and local components)
 * turns a one-day full-day booking into a two-day booking once the display is
 * shifted by the booking's timezone offset.
 */

/** Strips a trailing `Z` or `±HH:MM` so the value is parsed as local wall time. */
const stripTimezoneSuffix = (value: string): string =>
  value.replace('Z', '').replace(/[+-]\d{2}:\d{2}$/, '');

/**
 * Formats a Date as an ISO-like string using local components and no `Z`
 * suffix, matching how bookings are stored throughout the planner.
 */
export const formatLocalISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const ms = String(date.getMilliseconds()).padStart(3, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}`;
};

/** Whole calendar days between two dates, ignoring time-of-day and DST. */
const calendarDaysBetween = (from: Date, to: Date): number => {
  const fromUtc = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const toUtc = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((toUtc - fromUtc) / 86_400_000);
};

/** Returns a copy of `date` moved by `days` calendar days, keeping its time-of-day. */
const shiftByDays = (date: Date, days: number): Date => {
  const shifted = new Date(date);
  shifted.setDate(shifted.getDate() + days);
  return shifted;
};

export interface PastedBookingTimes {
  startTime: string;
  endTime: string;
}

/**
 * Returns the start/end the pasted copy of a booking should be stored with.
 *
 * Both edges move by the same number of calendar days — the offset between the
 * copied booking's start day and the day the paste happened — so the copy keeps
 * the source booking's day span and time-of-day for every duration type
 * (full-day, half-day and hourly).
 */
export function computePastedBookingTimes(
  source: { startTime: string; endTime: string },
  pasteDate: Date
): PastedBookingTimes {
  const sourceStart = new Date(stripTimezoneSuffix(source.startTime));
  const sourceEnd = new Date(stripTimezoneSuffix(source.endTime));

  if (Number.isNaN(sourceStart.getTime()) || Number.isNaN(sourceEnd.getTime())) {
    throw new Error(`Cannot paste booking with invalid times: ${source.startTime} – ${source.endTime}`);
  }

  const dayShift = calendarDaysBetween(sourceStart, pasteDate);

  return {
    startTime: formatLocalISO(shiftByDays(sourceStart, dayShift)),
    endTime: formatLocalISO(shiftByDays(sourceEnd, dayShift)),
  };
}
